import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatBRL } from "@/lib/format";
import { Users, ArrowUpRight, Warning, CheckCircle, Clock, CurrencyDollar, Crown, Medal, Trophy, ShieldStar } from "@phosphor-icons/react";
import { toast } from "sonner";

const Tile = ({ label, value, icon: Icon, accent = "zinc", testid }) => (
  <Card className="border-zinc-200 p-5 card-soft" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">{label}</div>
      <div className={`w-8 h-8 rounded-lg grid place-items-center bg-${accent}-50 text-${accent}-700`}>
        <Icon size={16} weight="duotone" />
      </div>
    </div>
    <div className="metric-num text-4xl mt-3 tab-nums">{value}</div>
  </Card>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/dashboard");
      setStats(data);
    } catch (e) { toast.error("Erro ao carregar dashboard"); }
  };

  useEffect(() => { load(); }, []);

  const runCron = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/cron/run");
      toast.success(`Notificações geradas: ${data.created} (clientes verificados: ${data.checked})`);
    } catch (e) { toast.error("Erro ao gerar notificações"); }
    finally { setBusy(false); }
  };

  if (!stats) return <div className="text-zinc-500">Carregando...</div>;

  return (
    <div>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-zinc-500 font-semibold">Visão Geral</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Dashboard</h1>
        </div>
        <Button onClick={runCron} disabled={busy} data-testid="dashboard-run-cron" className="bg-zinc-900 hover:bg-zinc-800 text-white">
          {busy ? "Gerando..." : "Gerar lembretes automáticos"}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Tile testid="tile-total" label="Clientes" value={stats.total_clients} icon={Users} accent="blue" />
        <Tile testid="tile-em-dia" label="Em dia" value={stats.em_dia} icon={CheckCircle} accent="emerald" />
        <Tile testid="tile-proximo" label="Próximo" value={stats.proximo} icon={Clock} accent="amber" />
        <Tile testid="tile-vencido" label="Vencidos" value={stats.vencido} icon={Warning} accent="rose" />
        <Tile testid="tile-inativo" label="Inativos" value={stats.inativo} icon={Users} accent="zinc" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <Card className="border-zinc-200 p-6 card-soft lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">Receita recorrente mensal</div>
              <div className="metric-num text-5xl mt-2">{formatBRL(stats.monthly_recurring_revenue)}</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">Últimos 30 dias</div>
              <div className="metric-num text-3xl mt-2 text-emerald-700 flex items-center gap-1 justify-end">
                {formatBRL(stats.revenue_30d)}
                <ArrowUpRight size={20} weight="bold"/>
              </div>
            </div>
          </div>
          <div className="h-px bg-zinc-200 my-6" />
          <div className="flex items-center gap-3 text-sm text-zinc-600">
            <CurrencyDollar size={18} className="text-emerald-600" weight="duotone"/>
            Soma do valor dos planos de clientes ativos. Atualizado em tempo real.
          </div>
        </Card>

        <Card className="border-zinc-200 p-6 card-soft">
          <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold mb-4">Distribuição por nível</div>
          <div className="space-y-3">
            <TierRow label="Bronze" tier="bronze" count={stats.loyalty_distribution.bronze} icon={Medal} />
            <TierRow label="Silver" tier="silver" count={stats.loyalty_distribution.silver} icon={ShieldStar} />
            <TierRow label="Gold"   tier="gold"   count={stats.loyalty_distribution.gold} icon={Trophy} />
            <TierRow label="King"   tier="king"   count={stats.loyalty_distribution.king} icon={Crown} />
          </div>
        </Card>
      </div>
    </div>
  );
};

const TierRow = ({ label, tier, count, icon: Icon }) => {
  const cls = { bronze: "tier-bronze", silver: "tier-silver", gold: "tier-gold", king: "tier-king" }[tier];
  return (
    <div className="flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg grid place-items-center ${cls}`}>
        <Icon size={16} weight="fill" />
      </div>
      <div className="flex-1">
        <div className="text-sm font-medium">{label}</div>
      </div>
      <div className="metric-num text-xl tab-nums" data-testid={`dist-${tier}`}>{count}</div>
    </div>
  );
};

export default Dashboard;
